import { defineStore } from "pinia";
import type LxcError from "~/core/models/LxcError.class";
import type { License } from "~/modules/license/models/License.interface";
import type { LicenseFeatureName } from "~/modules/license/models/LicenseFeatureName.enum";
import { LicenseStatus } from "~/modules/license/models/LicenseStatus.enum";
import { getLicenseStatus } from "~/modules/license/utils/getLicenseStatus";

export const useLicenseStore = defineStore("LicenceStore", () => {
  const _organizationCode: Ref<number | undefined> = ref();
  const activeLicenseInformation: Ref<License | undefined> = ref();
  const activeLicenseError: Ref<LxcError | undefined> = ref();

  function setActiveLicenseInformationAndError({
    organizationCode,
    license,
    error,
  }: {
    organizationCode?: number;
    license?: License;
    error?: LxcError;
  }) {
    _organizationCode.value = organizationCode;
    activeLicenseInformation.value = license;
    activeLicenseError.value = error;
  }

  /**
   * Return true if a feature is enabled and the license is valid
   * Otherwise return false
   * @param licenseFeature
   */
  function isLicenseFeatureEnabled(
    licenseFeature: LicenseFeatureName | string,
  ): boolean {
    return (
      isLicenseDeactivated() ||
      (isLicenseValid() &&
        !!activeLicenseInformation.value?.features?.find(
          (feature) => feature.name === licenseFeature && feature.enabled,
        ))
    );
  }

  /**
   * Return true if the license is deactivated, i.e. the license is disengaged and not applicable
   * Otherwise return false
   */
  function isLicenseDeactivated(): boolean {
    return activeLicenseInformation.value?.deactivated === true;
  }

  /**
   * Return true if the license is valid, i.e. the tolerated expiration date is not exceeded
   * Also return true if the license is deactivated
   * Otherwise return false
   */
  function isLicenseValid(): boolean {
    return (
      isLicenseDeactivated() ||
      (!!activeLicenseInformation.value?.toleratedUntil &&
        Date.now() <=
          new Date(activeLicenseInformation.value?.toleratedUntil).getTime())
    );
  }

  /**
   * Return true if the license expiration date is exceeded but not the tolerated expiration date
   * Otherwise return false
   */
  function isLicenseExpirationDateExceeded(): boolean {
    return (
      !!activeLicenseInformation.value &&
      getLicenseStatus(activeLicenseInformation.value) === LicenseStatus.WARNING
    );
  }

  /**
   * Return true if the count of at least one device type is strictly higher than the contracted limit but lower or equals the tolerated limit
   * Otherwise return false
   */
  function isLicenseDevicesCountExceeded(): boolean {
    return (
      (activeLicenseInformation.value?.deviceTypes || []).filter(
        (deviceType) =>
          deviceType.available !== undefined &&
          deviceType.contracted !== undefined &&
          deviceType.available > deviceType.contracted &&
          deviceType.tolerated !== undefined &&
          deviceType.available <= deviceType.tolerated,
      ).length > 0
    );
  }

  return {
    organizationCode: _organizationCode,
    activeLicenseInformation,
    activeLicenseError,
    setActiveLicenseInformationAndError,
    isLicenseFeatureEnabled,
    isLicenseDeactivated,
    isLicenseValid,
    isLicenseExpirationDateExceeded,
    isLicenseDevicesCountExceeded,
  };
});
